import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import { concat } from '@ember/helper';
import { VStack } from 'tio-ui/components/layout';
import { Heading, Text } from 'tio-ui/components/typography';
import { Chip } from 'tio-ui/components/buttons';
import { gte, notEq } from 'tio-ui/utilities';
import {
  PhTrophy,
  PhTrendUp,
  PhTrendDown,
  PhMinus,
  PhChartLine,
  PhLightning,
  PhTarget,
  PhPiggyBank,
  PhBook,
  PhUsers,
  PhMedal,
  PhCrown,
  PhStar,
  PhRocketLaunch,
  PhGraduationCap,
} from 'ember-phosphor-icons';
import { getCurrentDemoClientData } from 'tio-employee/utils/career-pathways-demo-data';
import StatCard from 'tio-employee/components/reimagined/stat-card';
import SectionHeader from 'tio-employee/components/reimagined/section-header';
import BoostActionLink from 'tio-employee/components/reimagined/boost-action-link';
import type {
  CareerCapitalScore,
  ScoreHistoryPoint,
} from 'tio-employee/services/career-pathways-api';

interface ScoreModel {
  score: CareerCapitalScore;
  history: ScoreHistoryPoint[];
}

interface CareerCapitalScoreSignature {
  Args: {
    model: ScoreModel;
  };
}

export default class CareerCapitalScorePage extends Component<CareerCapitalScoreSignature> {
  get demoClient() {
    return getCurrentDemoClientData();
  }

  get score() {
    return this.args.model.score;
  }

  get history() {
    return this.args.model.history;
  }

  get scoreChange30Days() {
    if (this.history.length < 2) return 0;
    const current = this.history[this.history.length - 1];
    const thirtyDaysAgo = this.history[Math.max(0, this.history.length - 4)];
    return current && thirtyDaysAgo ? current.score - thirtyDaysAgo.score : 0;
  }

  get levelConfig() {
    const configs = {
      bronze: {
        color: 'from-amber-600 to-orange-700',
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-700',
        icon: PhMedal,
        label: 'Bronze',
        minScore: 0,
        maxScore: 499,
      },
      silver: {
        color: 'from-gray-400 to-gray-600',
        bgColor: 'bg-gray-50',
        borderColor: 'border-gray-300',
        textColor: 'text-gray-700',
        icon: PhStar,
        label: 'Silver',
        minScore: 500,
        maxScore: 749,
      },
      gold: {
        color: 'from-yellow-400 to-amber-500',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-300',
        textColor: 'text-yellow-700',
        icon: PhCrown,
        label: 'Gold',
        minScore: 750,
        maxScore: 899,
      },
      platinum: {
        color: 'from-violet-400 to-purple-600',
        bgColor: 'bg-violet-50',
        borderColor: 'border-violet-300',
        textColor: 'text-violet-700',
        icon: PhTrophy,
        label: 'Platinum',
        minScore: 900,
        maxScore: 1000,
      },
    };
    return configs[this.score.level] || configs.bronze;
  }

  get scoreProgressPercent() {
    const config = this.levelConfig;
    const range = config.maxScore - config.minScore;
    const progress = this.score.totalScore - config.minScore;
    return Math.min(100, Math.max(0, (progress / range) * 100));
  }

  get scoreProgressStyle() {
    return htmlSafe(`width: ${this.scoreProgressPercent}%`);
  }

  get nextLevelProgress() {
    if (!this.score.nextMilestone) return 0;
    const targetScore = this.score.nextMilestone.targetScore;
    const currentLevelStart = this.levelConfig.minScore;
    const totalRange = targetScore - currentLevelStart;
    const currentProgress = this.score.totalScore - currentLevelStart;
    return Math.round((currentProgress / totalRange) * 100);
  }

  get nextLevelProgressStyle() {
    return htmlSafe(`width: ${this.nextLevelProgress}%`);
  }

  get componentsList() {
    const components = this.score.components;
    return [
      {
        key: 'skillVelocity',
        name: 'Skill Velocity',
        icon: PhLightning,
        color: 'text-blue-600',
        bgColor: 'bg-blue-100',
        data: components.skillVelocity,
      },
      {
        key: 'careerReadiness',
        name: 'Career Readiness',
        icon: PhTarget,
        color: 'text-violet-600',
        bgColor: 'bg-violet-100',
        data: components.careerReadiness,
      },
      {
        key: 'financialOptimization',
        name: 'Financial Optimization',
        icon: PhPiggyBank,
        color: 'text-emerald-600',
        bgColor: 'bg-emerald-100',
        data: components.financialOptimization,
      },
      {
        key: 'learningProgress',
        name: 'Learning Progress',
        icon: PhBook,
        color: 'text-amber-600',
        bgColor: 'bg-amber-100',
        data: components.learningProgress,
      },
      {
        key: 'networkDensity',
        name: 'Network Density',
        icon: PhUsers,
        color: 'text-pink-600',
        bgColor: 'bg-pink-100',
        data: components.networkDensity,
      },
    ];
  }

  get recentBadges() {
    return this.score.badges.slice(0, 4);
  }

  get historyChartData() {
    const lastSix = this.history.slice(-6);
    const maxScore = Math.max(...lastSix.map((h) => h.score));
    const minScore = Math.min(...lastSix.map((h) => h.score));
    const range = maxScore - minScore || 1;

    return lastSix.map((point) => ({
      ...point,
      height: Math.round(((point.score - minScore) / range) * 100) + 20,
      formattedDate: new Intl.DateTimeFormat('en-US', { month: 'short' }).format(
        new Date(point.date)
      ),
    }));
  }

  getTrendIcon = (trend: string) => {
    if (trend === 'up') return PhTrendUp;
    if (trend === 'down') return PhTrendDown;
    return PhMinus;
  };

  getTrendColor = (trend: string) => {
    if (trend === 'up') return 'text-emerald-600';
    if (trend === 'down') return 'text-red-600';
    return 'text-gray-500';
  };

  getScoreColor = (score: number) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-blue-600';
    if (score >= 40) return 'text-amber-600';
    return 'text-red-600';
  };

  formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="data-audit w-full min-w-0">
      {{! Hero Score Card }}
      <div
        class="bg-gradient-to-r
          {{this.levelConfig.color}}
          rounded-xl p-6 text-white mb-6 relative overflow-hidden"
      >
        {{! Background decoration }}
        <div class="absolute top-0 right-0 opacity-10">
          <PhTrophy @size={{200}} />
        </div>

        <div class="relative z-10">
          <div class="flex items-center gap-3 mb-2">
            {{#let this.levelConfig.icon as |LevelIcon|}}
              <LevelIcon @size={{32}} />
            {{/let}}
            <Heading @level={{1}} class="text-white text-2xl">Career Capital Score</Heading>
          </div>

          <div class="flex items-end gap-8 mt-4">
            <div>
              <div class="text-7xl font-bold">{{this.score.totalScore}}</div>
              <Text class="text-white/80 text-lg">of 1,000 points</Text>
            </div>

            <div class="flex-1 max-w-md">
              <div class="flex justify-between items-center mb-2">
                <Chip class="bg-white/20 text-white border-0">{{this.levelConfig.label}}
                  Level</Chip>
                <Text class="text-white/80">Top {{this.score.percentile}}%</Text>
              </div>

              {{! Progress to next level }}
              <div class="h-3 bg-white/20 rounded-full overflow-hidden">
                <div
                  class="h-full bg-white rounded-full transition-all"
                  style={{this.nextLevelProgressStyle}}
                ></div>
              </div>

              {{#if this.score.nextMilestone}}
                <div class="flex justify-between mt-2 text-white/80 text-sm">
                  <span>{{this.score.nextMilestone.pointsNeeded}}
                    pts to
                    {{this.score.nextMilestone.level}}</span>
                  <span>Est. {{this.formatDate this.score.nextMilestone.estimatedDate}}</span>
                </div>
              {{/if}}
            </div>
          </div>
        </div>
      </div>

      {{! Quick Stats Row }}
      <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          @icon={{PhChartLine}}
          @label="Company Rank"
          @value="#{{this.score.leaderboard.rank}}"
          @color="violet"
        >
          <:extra>
            <Text @intent="muted" class="text-sm">of
              {{this.score.leaderboard.totalParticipants}}</Text>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhUsers}}
          @label="Team Rank"
          @value="#{{this.score.leaderboard.teamRank}}"
          @color="blue"
        >
          <:extra>
            <Text @intent="muted" class="text-sm">of {{this.score.leaderboard.teamSize}}</Text>
          </:extra>
        </StatCard>

        <StatCard
          @icon={{PhTrendUp}}
          @label="30-Day Change"
          @value="+{{this.scoreChange30Days}} points"
          @color="emerald"
          @valueClass="text-emerald-600"
        />

        <StatCard
          @icon={{PhMedal}}
          @label="Badges Earned"
          @value="{{this.score.badges.length}}"
          @subtext="achievements"
          @color="amber"
        />
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {{! Score Breakdown }}
        <div class="lg:col-span-2 bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <div class="flex items-center justify-between mb-6">
            <Heading @level={{2}} class="text-lg font-semibold text-gray-900">Score Breakdown</Heading>
            <Text @intent="muted" class="text-sm">5 components weighted by impact</Text>
          </div>

          <div class="space-y-5">
            {{#each this.componentsList as |component|}}
              <div class="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                <div class="flex items-center justify-between mb-2">
                  <div class="flex items-center gap-3">
                    <div class="p-2 rounded-lg {{component.bgColor}} {{component.color}}">
                      {{#let component.icon as |Icon|}}
                        <Icon @size={{20}} />
                      {{/let}}
                    </div>
                    <div>
                      <Text class="font-medium text-gray-900">{{component.name}}</Text>
                      <Text @intent="muted" class="text-xs">{{component.data.weight}}% weight</Text>
                    </div>
                  </div>

                  <div class="flex items-center gap-3">
                    <div
                      class="flex items-center gap-1 {{this.getTrendColor component.data.trend}}"
                    >
                      {{#let (this.getTrendIcon component.data.trend) as |TrendIcon|}}
                        <TrendIcon @size={{16}} />
                      {{/let}}
                    </div>
                    <div class="text-2xl font-bold {{this.getScoreColor component.data.score}}">
                      {{component.data.score}}
                    </div>
                  </div>
                </div>

                {{! Component progress bar }}
                <div class="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    class="h-full rounded-full transition-all
                      {{if
                        (gte component.data.score 80)
                        'bg-emerald-500'
                        (if
                          (gte component.data.score 60)
                          'bg-blue-500'
                          (if (gte component.data.score 40) 'bg-amber-500' 'bg-red-500')
                        )
                      }}"
                    style={{htmlSafe (concat "width: " component.data.score "%")}}
                  ></div>
                </div>

                {{! Factors }}
                <div class="mt-3 flex flex-wrap gap-2">
                  {{#each component.data.factors as |factor|}}
                    <div
                      class="text-xs px-2 py-1 rounded
                        {{if
                          (gte factor.value factor.benchmark)
                          'bg-emerald-50 text-emerald-700'
                          'bg-amber-50 text-amber-700'
                        }}"
                    >
                      {{factor.name}}:
                      {{factor.value}}
                      {{#if (notEq factor.benchmark factor.value)}}
                        <span class="text-gray-400">(target: {{factor.benchmark}})</span>
                      {{/if}}
                    </div>
                  {{/each}}
                </div>
              </div>
            {{/each}}
          </div>
        </div>

        {{! Right Column }}
        <div class="space-y-6">
          {{! Recent Badges }}
          <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div class="flex items-center justify-between mb-4">
              <Heading @level={{3}} class="text-lg font-semibold text-gray-900">Recent Badges</Heading>
              <Chip @intent="default">{{this.score.badges.length}} total</Chip>
            </div>

            <div class="space-y-3">
              {{#each this.recentBadges as |badge|}}
                <div class="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div class="p-2 bg-amber-100 rounded-lg text-amber-600">
                    <PhTrophy @size={{24}} />
                  </div>
                  <div class="flex-1">
                    <Text class="font-medium text-gray-900">{{badge.name}}</Text>
                    <Text @intent="muted" class="text-xs">{{badge.description}}</Text>
                  </div>
                  <Text @intent="muted" class="text-xs">{{this.formatDate badge.earnedDate}}</Text>
                </div>
              {{/each}}
            </div>
          </div>

          {{! Score History Mini Chart }}
          <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <Heading @level={{3}} class="text-lg font-semibold text-gray-900 mb-4">Score History</Heading>

            <div class="flex items-end justify-between h-32 gap-2">
              {{#each this.historyChartData as |point|}}
                <div class="flex-1 flex flex-col items-center">
                  <div
                    class="w-full bg-gradient-to-t from-violet-500 to-violet-400 rounded-t-sm transition-all"
                    style={{htmlSafe (concat "height: " point.height "%")}}
                  ></div>
                  <Text @intent="muted" class="text-xs mt-2">{{point.formattedDate}}</Text>
                </div>
              {{/each}}
            </div>
          </div>

          {{! Quick Actions }}
          <div
            class="bg-gradient-to-r from-violet-50 to-purple-50 rounded-lg p-6 border border-violet-200"
          >
            <SectionHeader
              @icon={{PhRocketLaunch}}
              @title="Boost Your Score"
              @iconClass="text-violet-600"
              @iconSize={{24}}
              @marginClass="mb-3"
            />

            <div class="space-y-2">
              <BoostActionLink
                @route="authenticated.reimagined.skills"
                @icon={{PhLightning}}
                @label="Complete a skill assessment"
                @points="+15 pts"
                @iconClass="text-blue-600"
              />

              <BoostActionLink
                @route="authenticated.reimagined.recommendations"
                @icon={{PhGraduationCap}}
                @label="Start a recommended course"
                @points="+25 pts"
                @iconClass="text-violet-600"
              />

              <BoostActionLink
                @route="authenticated.reimagined.finance"
                @icon={{PhPiggyBank}}
                @label="Optimize Section 127"
                @points="+20 pts"
                @iconClass="text-emerald-600"
              />
            </div>
          </div>
        </div>
      </div>
    </VStack>
  </template>
}
